(() => {
  if (window.eggSubscriptionRedirectInitialized) return;

  window.eggSubscriptionRedirectInitialized = true;

  const PRODUCT_SELECTOR = 'product-info[data-egg-subscription-redirect-enabled="true"]';
  const SUBMIT_BUTTON_SELECTOR = 'button.product-form__submit[name="add"]';
  const CTA_TEXT = 'Add eggs & continue';
  const CTA_LOADING_TEXT = 'Adding eggs…';
  const REQUEST_TIMEOUT_MS = 20000;
  const forwardedButtons = new WeakSet();
  const requestTimeouts = new WeakMap();
  let redirectStarted = false;

  function findProductInfo(productId) {
    return Array.from(document.querySelectorAll(PRODUCT_SELECTOR)).find(
      (productInfo) => productInfo.dataset.productId === productId
    );
  }

  function findFormValue(productInfo, productId, name) {
    return Array.from(productInfo.querySelectorAll('form[data-loop-product-id]'))
      .filter((form) => form.dataset.loopProductId === productId)
      .map((form) => form.querySelector(`[name="${name}"]`)?.value)
      .find(Boolean);
  }

  function findSellingPlanText(productInfo, productId) {
    const sellingPlanId = findFormValue(productInfo, productId, 'selling_plan');

    if (sellingPlanId) {
      const matchingOption = Array.from(productInfo.querySelectorAll('.loop-selling-plan-selector option')).find(
        (option) => option.value === sellingPlanId
      );

      if (matchingOption) return matchingOption.textContent.trim();
    }

    const sellingPlanSelects = Array.from(productInfo.querySelectorAll('.loop-selling-plan-selector'));
    const visibleSellingPlanSelect = sellingPlanSelects.find((select) => select.offsetParent !== null);
    const selectedOption = (visibleSellingPlanSelect || sellingPlanSelects[0])?.selectedOptions?.[0];

    if (selectedOption) return selectedOption.textContent.trim();

    return (
      productInfo.querySelector('.loop-widget-sp-button.loop-widget-sp-button-selected')?.textContent.trim() || ''
    );
  }

  function findSelectedEggCount(productInfo) {
    return productInfo.querySelector('variant-selects input[type="radio"]:checked')?.value?.trim() || '';
  }

  function findProductTitle(productInfo) {
    return productInfo.querySelector('.product__title h1')?.textContent.trim() || '';
  }

  function getBundleUrl(productInfo, sellingPlanText) {
    const normalizedText = sellingPlanText.toLowerCase().replace(/\s+/g, ' ').trim();

    if (
      normalizedText.includes('every 2 weeks') ||
      normalizedText.includes('every two weeks') ||
      normalizedText.includes('fortnight')
    ) {
      return productInfo.dataset.eggUpsellBundleUrlFortnightly;
    }

    if (normalizedText.includes('every week') || normalizedText.includes('weekly')) {
      return productInfo.dataset.eggUpsellBundleUrlWeekly;
    }

    return '';
  }

  function normalizeUrl(url) {
    if (!url) return '';

    try {
      const destination = new URL(url, window.location.origin);
      return destination.protocol === 'https:' ? destination.href : '';
    } catch (_error) {
      return '';
    }
  }

  function findSubmitButton(productInfo) {
    return productInfo.querySelector(SUBMIT_BUTTON_SELECTOR);
  }

  function setButtonText(button, text) {
    const textContainer = button?.querySelector('span');

    if (textContainer && textContainer.textContent !== text) {
      textContainer.textContent = text;
    }
  }

  function setButtonLoading(productInfo, isLoading) {
    const button = findSubmitButton(productInfo);
    if (!button) return;

    productInfo.toggleAttribute('data-egg-subscription-redirect-loading', isLoading);

    if (isLoading) {
      button.dataset.eggRedirectWasDisabled = String(button.disabled);
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      setButtonText(button, CTA_LOADING_TEXT);
      return;
    }

    const previousDisabledState = button.dataset.eggRedirectWasDisabled;
    const shouldRemainDisabled =
      previousDisabledState === 'true' || (previousDisabledState === undefined && button.disabled);

    delete button.dataset.eggRedirectWasDisabled;
    button.disabled = shouldRemainDisabled;
    button.toggleAttribute('aria-disabled', shouldRemainDisabled);
    button.removeAttribute('aria-busy');
    if (!shouldRemainDisabled) setButtonText(button, CTA_TEXT);
  }

  function syncButtonText(productInfo) {
    const button = findSubmitButton(productInfo);
    if (!button) return;

    if (productInfo.hasAttribute('data-egg-subscription-redirect-loading')) {
      setButtonText(button, CTA_LOADING_TEXT);
    } else if (!button.disabled) {
      setButtonText(button, CTA_TEXT);
    }
  }

  function clearRequestTimeout(productInfo) {
    const timeoutId = requestTimeouts.get(productInfo);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      requestTimeouts.delete(productInfo);
    }
  }

  function clearError(productInfo) {
    const errorWrapper = productInfo.querySelector('.product-form__error-message-wrapper');
    if (errorWrapper) errorWrapper.hidden = true;
  }

  function showError(productInfo, message) {
    clearRequestTimeout(productInfo);
    setButtonLoading(productInfo, false);

    const errorWrapper = productInfo.querySelector('.product-form__error-message-wrapper');
    const errorMessage = errorWrapper?.querySelector('.product-form__error-message');

    if (!errorWrapper || !errorMessage) return;

    errorMessage.textContent = message;
    errorWrapper.hidden = false;
    errorWrapper.tabIndex = -1;
    errorWrapper.focus();
  }

  function getCartUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart.js`;
  }

  async function fetchCart() {
    const response = await window.fetch(getCartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

    return response.json();
  }

  function normalizeText(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function cartHasMatchingSubscription(cart, productId, variantId, sellingPlanId, productTitle, eggCount) {
    return Array.isArray(cart?.items)
      ? cart.items.some(
          (item) => {
            if (String(item.selling_plan_allocation?.selling_plan?.id || '') !== sellingPlanId) return false;

            const isDirectProductMatch =
              String(item.product_id) === productId && String(item.variant_id) === variantId;
            const bundleName = item.properties?._bundleName || item.properties?.bundleName;
            const presetBundleFlag = item.properties?._isPresetBundleProduct;
            const isMappedPresetBundleMatch =
              (presetBundleFlag === true || presetBundleFlag === 'true') &&
              normalizeText(bundleName) === normalizeText(productTitle) &&
              Number(item.quantity) === Number(eggCount);

            return isDirectProductMatch || isMappedPresetBundleMatch;
          }
        )
      : false;
  }

  function redirectToBundle(productInfo, bundleUrl) {
    clearRequestTimeout(productInfo);
    redirectStarted = true;
    window.location.assign(bundleUrl);
  }

  function forwardClickToLoop(button, productInfo) {
    forwardedButtons.add(button);
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.click();
    forwardedButtons.delete(button);

    clearRequestTimeout(productInfo);
    requestTimeouts.set(
      productInfo,
      window.setTimeout(() => {
        if (!redirectStarted) {
          showError(productInfo, 'We could not add your eggs. Please try again.');
        }
      }, REQUEST_TIMEOUT_MS)
    );
  }

  async function handleAddToCartClick(event, button, productInfo) {
    const productId = productInfo.dataset.productId || '';
    const variantId = String(findFormValue(productInfo, productId, 'id') || '');
    const sellingPlanId = String(findFormValue(productInfo, productId, 'selling_plan') || '');
    const productTitle = findProductTitle(productInfo);
    const eggCount = findSelectedEggCount(productInfo);
    const sellingPlanText = findSellingPlanText(productInfo, productId);
    const bundleUrl = normalizeUrl(getBundleUrl(productInfo, sellingPlanText));

    event.preventDefault();
    event.stopImmediatePropagation();

    if (redirectStarted || productInfo.hasAttribute('data-egg-subscription-redirect-loading')) return;

    clearError(productInfo);
    setButtonLoading(productInfo, true);

    if (!productId || !variantId || !sellingPlanId || !productTitle || !eggCount || !bundleUrl) {
      showError(productInfo, 'Please choose your eggs and delivery frequency, then try again.');
      return;
    }

    try {
      const cart = await fetchCart();

      if (cartHasMatchingSubscription(cart, productId, variantId, sellingPlanId, productTitle, eggCount)) {
        redirectToBundle(productInfo, bundleUrl);
        return;
      }

      forwardClickToLoop(button, productInfo);
    } catch (error) {
      console.error('Could not check the cart before adding the egg subscription.', error);
      showError(productInfo, 'We could not check your basket. Please try again.');
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(SUBMIT_BUTTON_SELECTOR);
      if (!button || forwardedButtons.has(button)) return;

      const productInfo = button.closest(PRODUCT_SELECTOR);
      if (!productInfo) return;

      handleAddToCartClick(event, button, productInfo);
    },
    true
  );

  document.addEventListener('loopPresetAddToCartSuccessEvent', (event) => {
    if (redirectStarted) return;

    const productId = String(event.detail?.productId || '');
    const productInfo = findProductInfo(productId);

    if (!productInfo) return;

    const response = event.detail?.response;
    if (Number(response?.status) >= 400) {
      showError(productInfo, response.description || response.message || 'We could not add your eggs. Please try again.');
      return;
    }

    const sellingPlanText = findSellingPlanText(productInfo, productId);
    const bundleUrl = normalizeUrl(getBundleUrl(productInfo, sellingPlanText));

    if (!bundleUrl) {
      console.error('Egg subscription bundle redirect is not configured for the selected frequency.', {
        productId,
        sellingPlanText,
      });
      showError(productInfo, 'Your eggs were added, but we could not continue. Please refresh the page and try again.');
      return;
    }

    redirectToBundle(productInfo, bundleUrl);
  });

  document.querySelectorAll(PRODUCT_SELECTOR).forEach((productInfo) => {
    syncButtonText(productInfo);

    const observer = new MutationObserver(() => syncButtonText(productInfo));
    observer.observe(productInfo, { childList: true, characterData: true, subtree: true });
  });

  window.addEventListener('pageshow', () => {
    redirectStarted = false;

    document.querySelectorAll(PRODUCT_SELECTOR).forEach((productInfo) => {
      clearRequestTimeout(productInfo);
      setButtonLoading(productInfo, false);
      clearError(productInfo);
    });
  });
})();
